<template>
  <div
    class="fixed inset-0 z-(--z-panel) bg-backdrop backdrop-blur-xs"
    @click="closeWindow"
  ></div>
  <div
    class="wrapper-create-task relative z-[calc(var(--z-panel)+1)] flex w-138 max-w-[98vw] max-h-[90vh]
           flex-col gap-3.5 overflow-y-auto rounded-tile border border-line bg-bg2 p-4 sm:p-5
           shadow-[var(--shadow-modal)] "
    ref="createTaskRef"
  >
    <div class="flex items-center justify-between">
      <div class="min-w-0 truncate text-lg font-medium text-text">
        {{ parentTaskId
          ? $t('addTask.create_subtask_for', { task: parentTaskTitle })
          : $t('addTask.create_task') }}
      </div>
      <button
        type="button"
        class="flex size-8 cursor-pointer items-center justify-center rounded-chip
               border-0 bg-transparent p-0 text-muted-soft transition-colors duration-150
               hover:bg-hover hover:text-accent"
        @click="closeWindow"
      >
        <IconXMark16Solid class="size-4 shrink-0" />
      </button>
    </div>
    <div v-if="images.length" class="grid grid-cols-3 gap-2">
      <div
        v-for="(item, index) in images"
        :key="item.id"
        class="relative aspect-[4/3] overflow-hidden rounded-chip bg-input"
      >
        <!-- the image itself, blurred, fills whatever the thumbnail leaves uncovered -->
        <img
          :src="item.image"
          alt=""
          class="absolute inset-0 size-full scale-110 object-cover blur-md"
        />
        <Picture
          cover
          color="transparent"
          class="relative"
          :extraImageStyle="thumbnailStyle(item)"
          :basePicture="item.image"
          :showTools="true"
          @deleteImage="images.splice(index, 1)"
          @editImage="editImage(item)"
        />
      </div>
      <button
        v-if="images.length < MAX_ATTACHED_IMAGES"
        type="button"
        class="flex aspect-[4/3] cursor-pointer items-center justify-center rounded-chip
               border-[1.5px] border-dashed border-line bg-transparent text-muted
               transition-colors duration-150 hover:border-accent hover:text-accent"
        :title="$t('addTask.select_picture')"
        @click="selectImgFromDevice"
      >
        <IconPlus class="size-6" />
      </button>
    </div>
    <div
      v-else
      class="image-container flex w-full items-center justify-center overflow-hidden
             text-center text-muted max-sm:h-0 max-sm:min-h-25 max-sm:flex-1"
    >
      <button
        v-if="mobileMode"
        type="button"
        class="select-image flex h-full w-full cursor-pointer flex-col items-center justify-center
               gap-2 rounded-card border-[1.5px] border-dashed border-line bg-transparent p-5
               text-body text-muted"
        @click="selectImgFromDevice"
      >
        <IconPhoto class="size-6 shrink-0" />
        {{ $t('addTask.tap_to_attach') }}
      </button>
      <div
        v-else
        class="select-image flex h-31.5 w-full flex-col items-center justify-center
               gap-2 rounded-card border-[1.5px] border-dashed border-line p-5 text-body text-muted"
      >
        <div>{{ $t('addTask.ctrl_v_paste') }}</div>
        <div>{{ $t('addTask.or') }}</div>
        <div>
          <SimpleButton
            size="sm"
            class="w-32.5 px-4! py-0! text-body!"
            :text="$t('addTask.select_picture')"
            @click="selectImgFromDevice"
          />
        </div>
      </div>
    </div>
    <div class="config-container relative contents max-sm:flex max-sm:flex-col max-sm:gap-3.5">
      <ProjectSelect
        v-if="!parentTaskId"
        v-model:projectId="selectedProjectId"
        :projects="projectsList"
      />
      <div class="tags-section flex flex-col gap-2 max-sm:order-2">
        <div class="tags-label text-label text-muted">
          {{ $t('addTask.tags') }}
        </div>
        <TagCombobox
          v-model:tags="selectedLabels"
          :availableTags="labelsByUsage"
          panelClass="dropdown-tags-content-create bg-popover-solid!
            shadow-[var(--shadow-dropdown)]!"
          @create="createLabel"
        />
      </div>
      <div class="wrapper-text max-sm:relative max-sm:order-1">
        <textarea
          v-model="textTasks"
          :placeholder="$t('addTask.placeholder')"
          ref="textArea"
          class="text-editor h-28 w-full resize-none rounded-card border border-line
                 bg-input px-3.5 py-3 text-body leading-normal text-text outline-none
                 [font-family:inherit] placeholder:text-muted"
          @blur="textAreaBlur"
          @keydown.enter.exact="createTask($event)"
          @keydown.shift.enter="(e) => {}"
          @keydown.ctrl.enter="textTasks += '\n'"
          @dragover.prevent
          @drop.prevent="dropHandler"
        ></textarea>
      </div>
      <div class="wrapper-bottom flex items-center gap-2.5 max-sm:order-3 max-sm:flex-wrap max-sm:gap-2">
        <div class="assign-section flex items-center">
          <Dropdown v-if="!loading"   
            @click.stop
            @visibilityChanged="assigneeDropdownOpen = $event"
            closeOnClick 
          >
            <template v-slot:wrapper>
              <div
                class="assign-button group flex h-9 cursor-pointer items-center gap-2
                       rounded-chip border border-line py-0 pr-3 pl-1.5 text-body font-medium
                       text-text whitespace-nowrap transition-colors duration-150
                       hover:border-accent"
              >
                <UserAvatar withDayoffBadge
                  class="user-avatar avatar size-6!"
                  :user="selectedAssignee"
                />
                <div class="assign-text flex flex-col items-center">
                  <div>{{ selectedAssignee.id ? '' : $t('search.unassigned') }}</div>
                  <div v-if="selectedAssignee.id" class="assign-username max-w-20 truncate">
                    {{ selectedAssignee.username }}
                  </div>
                </div>
                <i
                  class="tr tr-priority--1 flex size-4 shrink-0 items-center justify-center
                         text-label text-text-soft transition-[transform,color] duration-150
                         group-hover:text-accent"
                  :class="assigneeDropdownOpen ? 'rotate-180' : ''"
                ></i>
              </div>
            </template>
            <template v-slot:items>
              <AppSearchBox
                class="w-80"
                :items="arrayMembers"
                :getSearchText="user => userSearchText(user, t('task.Unassign'))"
                :placeholder="t('task.SEARCH_ASSIGNEE')"
                @select="setAssignUser"
              >
                <template #item="{ item }">
                  <UserAvatar withDayoffBadge :user="item?.id ? item : null" />
                  <div class="truncate text-left">{{ item?.id ? item.username : t('task.Unassign') }}</div>
                </template>
              </AppSearchBox>
            </template>
          </Dropdown>
          <div v-else class="assign-skeleton ml-2.5">
            <SkeletonLoader type="avatar" />
          </div>
        </div>
        <div class="assign-section prio flex items-center">
          <div
            class="task-prio-wrapper flex h-9 items-center gap-2.5 rounded-chip
                   border border-line px-3 text-label font-bold text-text whitespace-nowrap
                   max-sm:gap-1.5 max-sm:px-2"
          >
            <div
              class="prio-button low clickable flex size-4 items-center justify-center
                     transition-colors duration-150 hover:text-accent"
              :class="priority === -1 ? 'active text-accent' : 'text-muted'"
              @click="updatePriority(-1)"
            >
              <i class="tr tr-priority--1"></i>
            </div>
            <div
              class="prio-button usual clickable flex items-center justify-center text-text
                     transition-colors duration-150 hover:text-accent"
              :class="priority === 0 ? 'active' : ''"
              @click="updatePriority(0)"
            >
              {{ $t('task.USUAL') }}
            </div>
            <div
              class="prio-button high clickable flex size-4 items-center justify-center
                     transition-colors duration-150 hover:text-accent"
              :class="priority > 0 ? 'active text-accent' : 'text-muted'"
              @click="updatePriority(1)"
            >
              <i class="tr" :class="`tr-priority-${priority < 1 ? 1 : priority}`"> </i>
            </div>
          </div>
        </div>
        <div class="create-button ml-auto">
          <SimpleButton
            variant="accent"
            class="button-create h-10! min-h-10! rounded-chip! px-5! py-0! text-body! font-bold! max-sm:hidden!"
            :disabled="!images.length && !textTasks.trim()"
            :text="$t('addTask.create_task')"
            @click="createTask"
          />
          <SimpleButton
            variant="accent"
            size="icon"
            class="hidden! max-sm:flex!"
            :title="$t('addTask.create_task')"
            :disabled="!images.length && !textTasks.trim()"
            @click="createTask"
          >
            <IconPaperAirplane16Solid class="size-4" />
          </SimpleButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import SimpleButton from '@/components/elements/SimpleButton';
import ProjectSelect from '@/components/widgets/ProjectSelect';
import TagCombobox from '@/components/widgets/TagCombobox.vue';
import SkeletonLoader from '@/components/elements/SkeletonLoader';
import eventBus from '@/eventBus';
import Picture from '@/components/elements/Picture';
import AppSearchBox from '@/components/elements/AppSearchBox.vue';
import { IconPaperAirplane16Solid, IconPhoto, IconPlus, IconXMark16Solid } from '@iconify-prerendered/vue-heroicons';
import { userSearchText } from '@/utils';
import { editImageWithPainterro, readFileAsDataURL } from '@/store/utils';
import { MAX_ATTACHED_IMAGES, MAX_IMAGES_ALERT_ID } from '@/utils/constantHolder';


const props = defineProps({
  stageId: { type: String, default: null },
});
const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

// Reactive data
const images = ref([]);
const parentTaskId = ref(null);
// a subtask is created from its parent's dialog, so the parent is the task under edit
const parentTaskTitle = computed(() => store.state.project.taskUnderEdit.textMD.split('\n')[0]);
const selectedProjectId = ref(store.state.project.project?.id ?? null);
const selectedLabels = ref([]);
const labelsSelectedProject = ref([]);
const membersSelectedProject = ref([]);
const textTasks = ref('');
const loading = ref(false);
const selectedAssignee = ref({ id: null });
const priority = ref(0);
const painterroOpened = ref(false);

// Template refs
const textArea = ref(null);
const createTaskRef = ref(null);
const assigneeDropdownOpen = ref(false);

// Computed properties
const projectsList = computed(() => store.state.project.projectsList);
const project = computed(() => store.state.project.project);
const user = computed(() => store.state.user.user);
const mobileMode = computed(() => store.state.system.mobileMode);


function textAreaBlur(event) {
  // a dropdown of this modal autofocuses its own search field, and the tag field is
  // typed into in place — leave the focus there instead of pulling it straight back
  if (event.relatedTarget?.closest('.drop-content-root, .tags-section')) {
    return;
  }
  if (textArea.value && !painterroOpened.value && !assigneeDropdownOpen.value) {
    // painterro buttons doesw not work if some input is focused that is why we blur it
    textArea.value.focus();
  }
}

const selectedProject = computed(
  () => projectsList.value.find(p => p.id === selectedProjectId.value),
);

// most-used tags first; the combobox drops the already-picked ones from the suggestions
const labelsByUsage = computed(() => [...labelsSelectedProject.value]
  .sort((a, b) => (b.tasksCount - a.tasksCount) || a.name.localeCompare(b.name)));

const arrayMembers = computed(() => {
  const list = [{ id: null }, ...membersSelectedProject.value];
  return list.filter(x => x.id !== selectedAssignee.value.id);
});

// Methods
const createLabel = ({ name, color }) => {
  const label = {
    id: window.utils.genId(),
    name,
    color,
    tasksCount: 0,
  };
  labelsSelectedProject.value.push(label);
  selectedLabels.value = [...selectedLabels.value, label];
};

const setAssignUser = (user) => {
  selectedAssignee.value = user;
};

const closeWindow = () => {
  emit('close');
};

const revealFocusedField = () => {
  document.activeElement.scrollIntoView({ block: 'nearest' });
};

const closeESC = (e) => {
  if (e.keyCode === 27 && !painterroOpened.value) {
    closeWindow();
  }
};

const setProjectInfo = async () => {
  if (!selectedProjectId.value) return;

  loading.value = true;
  const data = await store.dispatch('project/actionGetProjectInfo', {
    projectId: selectedProjectId.value,
    labels: true,
    members: true,
  });

  if (data.error) {
    return;
  }
  labelsSelectedProject.value = data.labels;
  membersSelectedProject.value = data.members;
  // Auto-select focused tag for current user if present
  const myMember = membersSelectedProject.value.find(m => m.id === user.value.id);
  const focusedTag = myMember?.tagFocus
    && labelsSelectedProject.value.find(l => l.id === myMember.tagFocus);
  if (focusedTag) {
    selectedLabels.value = [focusedTag];
  }
  loading.value = false;
};

const preselectParentLabels = () => {
  if (!parentTaskId.value) return;
  const parentTask = store.state.project.taskUnderEdit;
  if (!parentTask || parentTask.id !== parentTaskId.value || !Array.isArray(parentTask.labels)) {
    return;
  }
  const parentLabelIds = new Set(parentTask.labels.map(l => l.id));
  const parentLabels = labelsSelectedProject.value.filter(label => parentLabelIds.has(label.id));
  if (parentLabels.length) {
    selectedLabels.value = parentLabels;
  }
};

watch([loading, parentTaskId], ([isLoading, pid]) => {
  if (!isLoading && pid) {
    preselectParentLabels();
  }
});

// runs immediately for the project the modal was opened in, and again once the
// selector resolves a project for us (no project in context, list still loading)
watch(selectedProjectId, () => {
  selectedLabels.value = [];
  selectedAssignee.value = { id: null };
  setProjectInfo();
}, { immediate: true });

// a 4:3 tile shows the image across its full width; one wider than 20:3 would be under 20%
// of the tile tall, so it is framed at 20:3 and cropped at the sides instead
const MAX_THUMBNAIL_RATIO = 20 / 3;
const thumbnailStyle = ({ width, height }) => (
  width / height > MAX_THUMBNAIL_RATIO ? `aspect-ratio: ${MAX_THUMBNAIL_RATIO}` : ''
);

const IMAGE_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp', 'image/gif'];

// images beyond the limit are dropped with a notice, the ones that fit are kept
const addImage = ({ image, width, height }) => {
  if (images.value.length >= MAX_ATTACHED_IMAGES) {
    store.commit('system/showAlert', {
      message: t('task.max_images_reached', { limit: MAX_ATTACHED_IMAGES }),
      type: 'warning',
      alertId: MAX_IMAGES_ALERT_ID,
      timeout: 3,
      teleport: true,
    });
    return;
  }
  images.value.push({
    id: window.utils.genId(),
    image,
    width,
    height,
  });
};

const loadImage = dataUrl => new Promise((resolve) => {
  const imageEl = new Image();
  imageEl.onload = () => resolve({ image: dataUrl, width: imageEl.width, height: imageEl.height });
  imageEl.src = dataUrl;
});

// loaded together so the given order is kept — the first image becomes the preview
const addImageFiles = (files) => {
  Promise.all(files.map(file => readFileAsDataURL(file).then(loadImage)))
    .then(loaded => loaded.forEach(addImage))
    .catch((error) => {
      console.error('Error reading file:', error);
      alert(t('ui.create_task_error_reading_file'));
    });
};

const selectImgFromDevice = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = IMAGE_TYPES.join(',');
  input.multiple = true;
  input.click();

  input.onchange = () => {
    const files = Array.from(input.files);
    const imageFiles = files.filter(file => IMAGE_TYPES.includes(file.type));
    if (imageFiles.length < files.length) {
      alert(t('addTask.select_exactly_image'));
    }
    addImageFiles(imageFiles);
  };
};

const createTask = async (event) => {
  console.log('createTask CALL');
  if (painterroOpened.value) {
    // painterro will handle enter
    console.log('Now painterro is open so return, will not submit task')
    return;
  }
  // Guard for cases when createTask is called without an event (e.g., mobile button/icon clicks)
  if (event?.preventDefault) event.preventDefault();
  if (event?.shiftKey || event?.ctrlKey) {
    textTasks.value += '\n';
    return;
  }
  if (!images.value.length && !textTasks.value?.trim()) {
    closeWindow();
    return;
  }

  if (project.value && selectedProjectId.value === project.value.id && !project.value.stages?.length) {
    store.commit('system/showAlert', {
      message: t('board.need_stage_to_create_task'),
      type: 'warning',
      alertId: 'need_stage',
      timeout: 5,
      closable: true,
      teleport: true,
    });
    return;
  }

  const task = {
    projectId: selectedProjectId.value,
    stageId: props.stageId,
    text: textTasks.value,
    labels: selectedLabels.value,
    assigneeId: selectedAssignee.value.id,
    images: images.value,
    priority: priority.value,
    parentTaskId: parentTaskId.value || undefined,
  };

  closeWindow();
  await store.dispatch('project/actionCreateTaskFromPopup', task);
};

const openPainterro = (imageUrl, save) => {
  painterroOpened.value = true;
  textArea.value.blur(); // painterro toolbar buttons does not work if some input is focused

  editImageWithPainterro({
    imageUrl,
    onSave: (imageData) => {
      save({
        image: imageData.asDataURL(),
        width: imageData.getWidth(),
        height: imageData.getHeight(),
      });
      painterroOpened.value = false;
      textArea.value.focus();
    },
    onClose: () => {
      painterroOpened.value = false;
      textArea.value.focus();
    },
  });
};

const editImage = (item) => {
  openPainterro(item.image, edited => Object.assign(item, edited));
};

// as with comment images: a single pasted picture opens painterro to annotate it, while
// several pictures, a drop or a gif are added as they are
const processIncomingFiles = (items, source) => {
  console.log('🖼️ CreateTask processIncomingFiles');
  if (painterroOpened.value || window.painterroOpenedInstance) {
    return;
  }
  const files = Array.from(items)
    .filter(item => item.kind === 'file' && item.type.startsWith('image') && !item.type.includes('svg'))
    .map(item => item.getAsFile());
  const hasRoom = images.value.length < MAX_ATTACHED_IMAGES;
  if (hasRoom && source === 'paste' && files.length === 1 && files[0].type !== 'image/gif') {
    readFileAsDataURL(files[0]).then(dataUrl => openPainterro(dataUrl, addImage));
    return;
  }
  addImageFiles(files);
};

const dropHandler = (event) => {
  processIncomingFiles(event.dataTransfer.items, 'drop');
};

const pasteHandler = (event) => {
  console.log('🖼️ CreateTask pasteHandler', event);
  if (event.clipboardData.items[0].kind !== 'string') {
    event.preventDefault();
  }
  const { items } = event.clipboardData;
  processIncomingFiles(items, 'paste');
};

const updatePriority = (priorityValue) => {
  let nextPriority;
  if (priorityValue === 1) {
    if (priority.value < 1) {
      nextPriority = 1;
    } else {
      nextPriority = priority.value === 1 ? 2 : 1;
    }
  } else {
    nextPriority = priorityValue;
  }

  priority.value = nextPriority;
};

// Event Bus handling
onMounted(() => {
  document.addEventListener('paste', pasteHandler);
  window.addEventListener('keydown', closeESC);
  window.addEventListener('resize', revealFocusedField);
  textArea.value.focus();
  
  eventBus.$on('create-task-painterro', addImage);
  eventBus.$on('create-task-set-parent', (pid) => {
    parentTaskId.value = pid || null;
    if (pid) {
      // the selector is hidden for a subtask — it lives in its parent's project
      selectedProjectId.value = store.state.project.taskUnderEdit.projectId;
    }
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('paste', pasteHandler);
  window.removeEventListener('keydown', closeESC);
  window.removeEventListener('resize', revealFocusedField);
  parentTaskId.value = null;
});

// Click outside handling; dropdown panels are teleported out of the modal, and this runs in
// the capture phase — before their items' own click handlers (e.g. an image's Edit/Delete)
onClickOutside(createTaskRef, (e) => {
  if (!painterroOpened.value 
      && !e.target?.closest('[data-no-create-click-outside]')
      && !e.target?.closest('.drop-content-root')
      && !e.target?.closest('.dropdown-tags-content-create')
  ) {
    closeWindow();
  }
});
</script>

